'use client'

import Typography from '@mui/material/Typography'
import Image from 'next/image'
import { FC } from 'react'
import events from '../../constants/analytics'
import Banner from '../../shared/components/Banner'
import Breadcrumbs from '../../shared/components/Breadcrumbs'
import GridContainer from '../../shared/components/GridContainer'
import useViewPage from '../../shared/hooks/useViewPage'
import type { ArticleDetail } from '../../shared/lib/server/getArticles'
import { formatDate } from '../../shared/lib/utils'
import { colors } from '../../shared/theme/colors'
import StructuredText from './components/StructuredText'

export interface ArticlePageProps {
  article: ArticleDetail
}

const ArticlePage: FC<ArticlePageProps> = ({ article }) => {
  useViewPage(events.EVENTS.INIT.ViewArticleDetailPage)

  return (
    <>
      {article.coverImage ? (
        <div className="relative bg-green-900">
          <Image
            src={article.coverImage.url}
            alt={article.coverImage.alt ?? article.title}
            fill
            sizes="100vw"
            priority
            className="object-cover"
          />
          <div className="absolute inset-0 bg-green-900/60" />
          <GridContainer>
            <div className="relative col-start-1 col-span-full md:col-span-9 py-16 md:py-24">
              <Typography variant="h5" sx={{ color: colors.Green[400] }}>
                Mission Lane
              </Typography>
              <Typography variant="h1" sx={{ color: colors.White }} className="my-4">
                {article.title}
              </Typography>
            </div>
          </GridContainer>
        </div>
      ) : (
        <Banner title={article.title} label="Mission Lane" />
      )}
      <div className="bg-white pt-12 pb-8">
        <GridContainer>
          <div className="lg:col-start-2 col-span-full lg:col-span-9 flex flex-col gap-8">
            <Breadcrumbs
              links={[{ href: '/resources', label: 'Resources' }]}
              endLabel={article.title}
            />
            {article.publishedDate && (
              <Typography variant="label" sx={{ color: colors.Green[700] }}>
                {formatDate(article.publishedDate)}
              </Typography>
            )}
            {article.body ? (
              <StructuredText data={article.body} blocks={article.blocks} />
            ) : (
              <Typography variant="p1" sx={{ color: colors.Ink[700] }}>
                This article has no content yet. Check back soon.
              </Typography>
            )}
          </div>
        </GridContainer>
      </div>
    </>
  )
}

export default ArticlePage
